import React, { useState } from 'react';
import axios from 'axios';
import { formatNzDateTime } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Single PDF upload/replace/delete row used by GlobalSettingsPanel for both
 * the Terms of Trade and the Quality Assurance certifications.
 *
 * Props:
 *   - label:           Human label shown beside the row.
 *   - meta:            { present, filename, size, uploadedAt, uploadedBy } — `null` ok.
 *   - viewHref:        URL for the "View" button (e.g. /api/certifications/cert_xxx/file).
 *   - uploadUrl:       POST endpoint (e.g. /api/certifications/cert_xxx).
 *   - deleteUrl:       DELETE endpoint (defaults to uploadUrl if not provided).
 *   - onChanged:       Callback fired after upload or delete succeeds (parent refetches).
 *   - testIdPrefix:    Used to build data-testid attributes for QA scripts.
 *   - confirmDelete:   Custom confirm() copy. Defaults to a generic message.
 */
const PdfUploadRow = ({
  label, meta, viewHref, uploadUrl, deleteUrl,
  onChanged, testIdPrefix, confirmDelete,
}) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const present = !!meta?.present;
  const delUrl = deleteUrl || uploadUrl;

  const doUpload = async () => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(uploadUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg({ kind: 'ok', text: `${label} uploaded.` });
      setFile(null);
      onChanged?.();
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Upload failed' });
      setTimeout(() => setMsg(null), 5000);
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(
      confirmDelete || `Remove the current "${label}" PDF? It will no longer appear in the sidebar.`
    )) return;
    setMsg(null);
    try {
      await axios.delete(delUrl);
      setMsg({ kind: 'ok', text: `${label} removed.` });
      setFile(null);
      onChanged?.();
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Delete failed' });
      setTimeout(() => setMsg(null), 5000);
    }
  };

  return (
    <div className="border-t border-border-color/60 pt-3 mt-3 first:border-t-0 first:mt-0 first:pt-0">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-text-primary">{label}</p>
        {present && (
          <span className="text-[10px] text-success uppercase tracking-wider">Uploaded</span>
        )}
      </div>
      {present ? (
        <div className="mb-2 px-3 py-2 bg-black/30 border border-border-color rounded text-xs flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-text-primary truncate" data-testid={`${testIdPrefix}-filename`}>{meta.filename}</p>
            <p className="text-[10px] text-text-secondary">
              {(meta.size / 1024).toFixed(1)} KB · Uploaded{' '}
              {meta.uploadedAt ? formatNzDateTime(meta.uploadedAt) : '—'}
              {meta.uploadedBy ? ` by ${meta.uploadedBy}` : ''}
            </p>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <a
              href={viewHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}-view`}
              className="px-2.5 py-1 text-[11px] border border-border-color rounded hover:border-gold"
            >
              View
            </a>
            <button
              type="button"
              onClick={doDelete}
              data-testid={`${testIdPrefix}-delete`}
              className="px-2.5 py-1 text-[11px] border border-danger/40 text-danger rounded hover:bg-danger/10"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <p className="mb-2 px-3 py-2 text-[11px] text-text-secondary bg-black/20 border border-dashed border-border-color rounded">
          Not uploaded yet.
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid={`${testIdPrefix}-input`}
          className="flex-1 text-xs text-text-secondary file:mr-2 file:px-2.5 file:py-1 file:rounded file:border-0 file:bg-gold/20 file:text-gold file:cursor-pointer hover:file:bg-gold/30"
        />
        <button
          type="button"
          onClick={doUpload}
          disabled={!file || busy}
          data-testid={`${testIdPrefix}-upload`}
          className="px-3 py-1.5 text-xs font-bold bg-gold text-black rounded hover:bg-gold-hover disabled:opacity-50"
        >
          {busy ? 'Uploading…' : present ? 'Replace' : 'Upload'}
        </button>
      </div>
      {msg && (
        <p
          data-testid={`${testIdPrefix}-status`}
          className={`mt-2 text-[11px] px-2.5 py-1.5 rounded ${
            msg.kind === 'ok'
              ? 'text-success bg-success/10 border border-success/30'
              : 'text-danger bg-danger/10 border border-danger/30'
          }`}
        >
          {msg.text}
        </p>
      )}
    </div>
  );
};

export default PdfUploadRow;
